import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const colorOptionIndex = parseInt(this.dataset.colorOptionIndex);
    const variant = event.detail.resource;

    if (colorOptionIndex !== -1 && variant && variant.options) {
      const selectedColor = variant.options[colorOptionIndex]?.toLowerCase();
      if (selectedColor) {
        this.filterByColor(selectedColor);
        return; // Skip replacement if we handled it via filtering
      }
    }

    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Filters media by color hashtag.
   * @param {string} colorName - The name of the color.
   */
  filterByColor(colorName) {
    const hashtag = `#${colorName.toLowerCase()}`;
    const mediaElements = this.querySelectorAll('[data-variant-alt]');

    mediaElements.forEach((el) => {
      const alt = el.dataset.variantAlt?.toLowerCase() || '';
      if (alt.includes('#')) {
        if (alt.includes(hashtag)) {
          el.removeAttribute('hidden');
        } else {
          el.setAttribute('hidden', '');
        }
      } else {
        // Fallback: show images without hashtags
        el.removeAttribute('hidden');
      }
    });

    // Reset slideshow to first visible slide if it exists
    if (this.slideshow) {
      // Update the total count if using counter style
      const totalSpan = this.slideshow.querySelector('[ref="total"]');
      if (totalSpan) {
        const visibleSlidesCount = this.slideshow.slides.length;
        totalSpan.textContent = visibleSlidesCount;
      }
      this.slideshow.select(0, undefined, { animate: false });
    }

    // If there's a zoom dialog, we might need to update its thumbnails too
    // The querySelectorAll above already covers elements inside the zoom dialog if they are children of media-gallery
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
